// @flow

import type { LocaleType } from './locale';
import { DEFAULT_LOCALE } from './locale';

type SeperatorType = ',' | '.' | ' ';

// Safari does not support fully neither toLocaleString, neither Intl standard
function addSeparators(
  number: number,
  fractionDigits?: number = 2,
  decimalSeparator?: SeperatorType = ',',
  thousandsSeparator?: SeperatorType = '.'
) {
  const res = (
    fractionDigits ? toFixedCustom(number, fractionDigits) : Math.round(number)
  )
    .toString()
    .split('.');

  if (res[0].length > 3) {
    res[0] = res[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, thousandsSeparator);
  }
  if ((res[1] || '').length < fractionDigits) {
    res[1] = res[1] || '';
    res[1] += new Array(fractionDigits - res[1].length + 1).join('0');
  }
  return res.join(decimalSeparator);
}

// IE toFixed is buggy
function toFixedCustom(number, precision) {
  const mult = Math.pow(10, precision);
  return Math.round(number * mult) / mult;
}

function trimTrailingZeros(amount, decimalSeparator) {
  let zerosToOmit = 0;
  if (!amount.includes(decimalSeparator)) {
    return amount;
  }
  for (
    let i = amount.length - 1;
    i >= 0 && (amount[i] === '0' || amount[i] === decimalSeparator);
    i--
  ) {
    zerosToOmit++;

    if (amount[i] === decimalSeparator) break;
  }

  return amount.substr(0, amount.length - zerosToOmit);
}

export function formatNumber(
  amount: number,
  omitTrailingZeroes: boolean = true,
  numberOfDecimals: number,
  locale: LocaleType = DEFAULT_LOCALE
) {
  const decimalSeparator = getDecimalSeparator(locale);
  const thousandsSeparator = getThousandSeparator(locale);
  let formattedAmount = '';

  if (numberOfDecimals >= 0) {
    formattedAmount = addSeparators(
      amount,
      numberOfDecimals,
      decimalSeparator,
      thousandsSeparator
    );

    if (omitTrailingZeroes) {
      formattedAmount = trimTrailingZeros(formattedAmount, decimalSeparator);
    }
  } else {
    formattedAmount = addSeparators(
      amount,
      2,
      decimalSeparator,
      thousandsSeparator
    );
  }

  return formattedAmount;
}

export function formatCurrency(
  amount: number,
  omitTrailingZeroes: boolean,
  numberOfDecimals: number,
  isoCurrency: string,
  showSymbol: boolean,
  locale: LocaleType = DEFAULT_LOCALE
) {
  const currency = showSymbol
    ? getCurrencySymbol(isoCurrency, locale)
    : isoCurrency;

  if (numberOfDecimals === 2) {
    omitTrailingZeroes =
      omitTrailingZeroes && !Math.floor((amount * 100) % 100);
  }

  let formattedAmount = formatNumber(
    amount,
    omitTrailingZeroes,
    numberOfDecimals,
    locale
  );

  if (isoCurrency) {
    formattedAmount = combineAmountAndCurrency(
      formattedAmount,
      currency,
      locale
    );
  }

  return formattedAmount;
}

export function combineAmountAndCurrency(
  amount: string,
  currency: string,
  locale: LocaleType = DEFAULT_LOCALE
) {
  switch (locale) {
    case 'en_GB':
      return currency + ' ' + amount;
    default:
      return amount + ' ' + currency;
  }
}

export function getBaseCurrency(locale: LocaleType = DEFAULT_LOCALE) {
  switch (locale) {
    case 'da_DK':
    case 'en_DK':
      return 'DKK';
    case 'nb_NO':
    case 'en_NO':
      return 'NOK';
    case 'sv_SE':
    case 'en_SE':
      return 'SEK';
    case 'fi_FI':
    case 'en_FI':
    case 'sv_FI':
      return 'EUR';
    case 'en_GB':
      return 'GBP';
    default:
      return '';
  }
}

export function getCurrencySymbol(
  isoCurrency: string,
  locale: LocaleType = DEFAULT_LOCALE
) {
  switch (isoCurrency) {
    case 'DKK':
    case 'NOK':
    case 'SEK':
      return 'kr.';
    case 'GBP':
      return '£';
    case 'EUR':
      return getEuroCurrencySymbol(locale);
    default:
      return '';
  }
}

export function getEuroCurrencySymbol(locale: LocaleType = DEFAULT_LOCALE) {
  switch (locale) {
    case 'fi_FI':
      return 'euroa';
    default:
      return '€';
  }
}

export function parseNumber(
  numberString: string,
  locale: LocaleType = DEFAULT_LOCALE
) {
  if (numberString === '') {
    return 0;
  }

  const decimalSeparator = getDecimalSeparator(locale);
  const stringWithoutFormatting = numberString
    .replace(getThousandRegex(locale), '')
    .replace(decimalSeparator, '.');
  return parseFloat(stringWithoutFormatting);
}

function getThousandRegex(locale = DEFAULT_LOCALE) {
  let thousandsSeparator = getThousandSeparator(locale);
  if (thousandsSeparator === '.') {
    thousandsSeparator = '\\.';
  }
  return new RegExp(thousandsSeparator, 'g');
}

export function getDecimalSeparator(locale: LocaleType = DEFAULT_LOCALE) {
  switch (locale) {
    case 'da_DK':
    case 'nb_NO':
    case 'sv_SE':
    case 'sv_FI':
    case 'fi_FI':
      return ',';
    case 'en_DK':
    case 'en_GB':
    case 'en_FI':
      return '.';
    default:
      return ',';
  }
}

export function getThousandSeparator(locale: LocaleType = DEFAULT_LOCALE) {
  switch (locale) {
    case 'da_DK':
    case 'nb_NO':
    case 'sv_SE':
    case 'sv_FI':
      return '.';
    case 'en_DK':
    case 'en_GB':
    case 'en_FI':
      return ',';
    case 'fi_FI':
      return ' ';
    default:
      return '.';
  }
}
